/**
 * Shared field resolution for contract manufacturer list / buyer browse cards.
 * Aligns with ContractManufacturersBrowsePage + ContractManufacturerPublicProfile.
 */

const CERT_LABELS = {
  iso_9001_2015: "ISO 9001",
  iso_13485_2016: "ISO 13485",
  iatf_16949_2016: "IATF 16949",
  as9100d: "AS9100D",
  iso_14001_2015: "ISO 14001",
  iso_45001_2018: "ISO 45001",
  iso_22000_2018: "ISO 22000",
  iso_27001_2013: "ISO 27001",
  iso_50001_2018: "ISO 50001",
  iso_22301_2019: "ISO 22301",
  iso_20000_1_2018: "ISO 20000",
  sa_8000_2014: "SA 8000",
  ce_marking: "CE",
  fda_approval: "FDA",
  fcc_certification: "FCC",
  cb_scheme: "CB Scheme",
};

function periodSuffix(period) {
  const p = (period || "").toLowerCase().replace(/_/g, " ");
  if (!p) return "";
  if (p.includes("month")) return "/month";
  if (p.includes("week")) return "/week";
  if (p.includes("day")) return "/day";
  if (p.includes("year")) return "/year";
  if (p.includes("quarter")) return "/quarter";
  return `/${p.replace(/\s+/g, " ").trim()}`;
}

function formatCapacityFromParts(v, u, per) {
  if (v == null || v === "") return null;
  const str = String(v).trim();
  if (!str) return null;
  const num = Number(str.replace(/,/g, ""));
  const formatted = Number.isFinite(num) ? num.toLocaleString() : str;
  const unitPart = u && String(u).trim() ? ` ${String(u).trim()}` : "";
  const suffix = periodSuffix(per);
  return `${formatted}${unitPart}${suffix}`.trim();
}

function capacityFromProductRow(p) {
  if (!p || typeof p !== "object") return null;
  let v = p.productionCapacityValue ?? p.production_capacity_value;
  const u = (p.productionCapacityUnit || p.production_capacity_unit || "").trim();
  const per = p.productionCapacityPeriod || p.production_capacity_period || "";
  if ((v == null || v === "") && typeof p.productionCapacity === "string" && p.productionCapacity.trim()) {
    const raw = p.productionCapacity.trim();
    const extracted = raw.replace(/[^0-9.,]/g, "").replace(/,/g, "");
    if (extracted) v = extracted;
    if (!u && /unit/i.test(raw)) {
      return formatCapacityFromParts(v || raw, "units", per) || raw;
    }
    return formatCapacityFromParts(v, u, per) || raw;
  }
  return formatCapacityFromParts(v, u, per);
}

export function getCapacity(item) {
  const prod = item?.product_details || {};
  if (Array.isArray(prod.products)) {
    for (let i = 0; i < prod.products.length; i += 1) {
      const c = capacityFromProductRow(prod.products[i]);
      if (c) return c;
    }
  }
  const rootCap = capacityFromProductRow(prod);
  if (rootCap) return rootCap;

  const fd = item?.facility_details || {};
  const mv = fd.monthlyProductionCapacity ?? fd.monthly_production_capacity;
  if (mv != null && mv !== "") {
    const n = Number(String(mv).replace(/,/g, ""));
    if (Number.isFinite(n)) return `${n.toLocaleString()} units/month`;
  }

  const pd = item?.production_details || {};
  const capList = pd.capabilitiesList || pd.capabilities_list;
  if (Array.isArray(capList) && capList[0]) {
    const c0 = capList[0];
    const mv2 = c0.machineCapacityValue ?? c0.machine_capacity_value;
    const mu = c0.machineCapacity || c0.machine_capacity;
    const mp = c0.machineCapacityPeriod || c0.machine_capacity_period;
    const alt = formatCapacityFromParts(mv2, mu, mp);
    if (alt) return alt;
  }
  return null;
}

function leadDaysFromRow(row) {
  if (!row || typeof row !== "object") return null;
  const d = row.lead_time_days ?? row.leadTimeDays ?? row.days;
  if (d == null || d === "") return null;
  const days = Number(d);
  return Number.isFinite(days) ? days : null;
}

function firstLeadDaysFromList(lt) {
  if (!Array.isArray(lt)) return null;
  for (let i = 0; i < lt.length; i += 1) {
    const days = leadDaysFromRow(lt[i]);
    if (days != null) return days;
  }
  return null;
}

function formatResponseFromDays(days) {
  if (days == null || !Number.isFinite(days)) return null;
  if (days <= 1) return "< 24 hours";
  return `Within ${days} days`;
}

export function getResponseTime(item) {
  const fd = item?.facility_details || {};
  const pd = item?.production_details || {};
  const hours =
    fd.averageResponseTimeHours ??
    fd.average_response_time_hours ??
    pd.averageResponseTimeHours ??
    pd.average_response_time_hours;
  const h = hours != null && hours !== "" ? Number(hours) : NaN;
  if (Number.isFinite(h) && h > 0) return `< ${Math.ceil(h)} hours`;

  const prod = item?.product_details || {};
  const rootLists = [
    prod.lead_time_list,
    prod.leadTimeList,
    prod.lead_time?.items,
    Array.isArray(prod.lead_time) ? prod.lead_time : null,
  ];
  for (let i = 0; i < rootLists.length; i += 1) {
    const days = firstLeadDaysFromList(rootLists[i]);
    const formatted = formatResponseFromDays(days);
    if (formatted) return formatted;
  }

  if (Array.isArray(prod.products)) {
    for (let j = 0; j < prod.products.length; j += 1) {
      const p = prod.products[j];
      const plists = [p.leadTimeList, p.lead_time_list, p.lead_time?.items];
      for (let k = 0; k < plists.length; k += 1) {
        const days = firstLeadDaysFromList(plists[k]);
        const formatted = formatResponseFromDays(days);
        if (formatted) return formatted;
      }
      const single = p.leadTimeDays ?? p.lead_time_days ?? p.leadTimeDay ?? p.lead_time_day;
      if (single != null && single !== "") {
        const formatted = formatResponseFromDays(Number(single));
        if (formatted) return formatted;
      }
    }
  }

  const daysSingle = prod.leadTimeDays ?? prod.lead_time_days;
  if (daysSingle != null && daysSingle !== "") {
    return formatResponseFromDays(Number(daysSingle));
  }
  return null;
}

function workforceCount(v) {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function getWorkforceSummary(item) {
  const fd = item?.facility_details || {};
  const bracket = fd.workforceBracket ?? fd.workforce_bracket;
  if (bracket != null && String(bracket).trim()) return String(bracket).trim();

  const pd = item?.production_details || {};
  const p = workforceCount(pd.numberPermanentWorkers ?? pd.number_permanent_workers);
  const c = workforceCount(pd.numberContractWorkers ?? pd.number_contract_workers);
  const s = workforceCount(pd.numberSeasonalWorkers ?? pd.number_seasonal_workers);
  const parts = [p, c, s].filter((n) => n != null);
  if (parts.length === 0) return null;
  const sum = parts.reduce((a, b) => a + b, 0);
  return `${sum.toLocaleString()}+ Workers`;
}

export function getExperienceSummary(item) {
  const fd = item?.facility_details || {};
  const y =
    fd.yearOfConstruction ??
    fd.year_of_construction ??
    fd.yearEstablished ??
    fd.year_established ??
    fd.year_of_establishment;
  const n = parseInt(String(y || "").trim(), 10);
  if (!Number.isFinite(n) || n < 1900 || n > 2100) return null;
  const yearNow = new Date().getFullYear();
  const years = Math.max(0, yearNow - n);
  if (years === 0) return "< 1 Year";
  return `${years}+ Years`;
}

function resolveDimensionUnitLabel(rawUom, dimensionUnitOptions) {
  if (rawUom == null || rawUom === "") return "";
  const str = String(rawUom).trim();
  if (!str) return "";
  const opts = dimensionUnitOptions || [];
  const found = opts.find(
    (opt) => String(opt.value) === str || String(opt.label).toLowerCase() === str.toLowerCase(),
  );
  if (found?.label) return String(found.label).trim();
  if (/^\d+$/.test(str)) return "";
  return str;
}

function moqFromProductRow(p, dimensionUnitOptions) {
  if (!p || typeof p !== "object") return null;
  let moq = p.minimumOrderQuantity ?? p.moq_min;
  let rawUom = p.moqUom ?? p.moq_uom ?? "";
  if (moq == null || moq === "") {
    const nested = p.moq;
    if (nested && typeof nested === "object") {
      moq = nested.min_quantity ?? nested.minQuantity;
      rawUom = rawUom || (nested.unit_of_measurement ?? nested.unitOfMeasurement ?? "");
    } else if (typeof nested === "number") {
      moq = nested;
    }
  }
  const uomLabel = resolveDimensionUnitLabel(rawUom, dimensionUnitOptions);
  if (moq != null && moq !== "") {
    return uomLabel ? `${moq} ${uomLabel}`.trim() : String(moq);
  }
  return null;
}

export function getMoqSummary(item, dimensionUnitOptions) {
  const prod = item?.product_details || {};
  if (Array.isArray(prod.products)) {
    for (let i = 0; i < prod.products.length; i += 1) {
      const s = moqFromProductRow(prod.products[i], dimensionUnitOptions);
      if (s) return s;
    }
  }
  const root = moqFromProductRow(prod, dimensionUnitOptions);
  if (root) return root;

  const pd = item?.production_details || {};
  const fallback = pd.minimumOrderQuantity ?? pd.moq_min ?? pd.moq;
  if (fallback != null && fallback !== "" && typeof fallback !== "object") {
    return String(fallback);
  }
  return null;
}

export function getCertificationShortLabels(item, max = 4) {
  const pd = item?.production_details || {};
  const raw = pd.qualityCertificationAvailable ?? pd.quality_certification_available ?? [];
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const out = [];
  const seen = new Set();
  for (let i = 0; i < raw.length && out.length < max; i += 1) {
    const key = typeof raw[i] === "string" ? raw[i] : raw[i]?.value;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const label = CERT_LABELS[key] || String(key).replace(/_/g, " ");
    out.push(label);
  }
  return out;
}

export function getQuoteSubmittedMeta(item) {
  const rawDate =
    item?.quoteSubmittedAt ??
    item?.quote_submitted_at ??
    item?.submittedQuoteAt ??
    item?.rfq_submitted_at;
  let dateStr = null;
  if (rawDate) {
    const d = new Date(rawDate);
    if (!Number.isNaN(d.getTime())) {
      dateStr = d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    }
  }
  const amt =
    item?.submittedQuoteAmount ??
    item?.quote_amount ??
    item?.last_quote_total ??
    item?.submitted_quote_amount;
  let priceStr = null;
  if (amt != null && amt !== "") {
    const n = Number(String(amt).replace(/,/g, ""));
    if (Number.isFinite(n)) {
      priceStr = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(n);
    } else {
      priceStr = String(amt);
    }
  }
  return { dateStr, priceStr };
}

export function formatMetric(value) {
  return value == null || value === "" ? "—" : value;
}

function normalizeImageRef(ref) {
  if (ref == null || ref === "") return null;
  if (typeof ref === "string") {
    const trimmed = ref.trim();
    return trimmed || null;
  }
  if (typeof ref === "object") {
    const url =
      ref.url ||
      ref.accessPath ||
      ref.filePath ||
      ref.src ||
      ref.path ||
      ref.imageUrl ||
      ref.image_url;
    return url != null && String(url).trim() ? String(url).trim() : null;
  }
  return null;
}

export function getCardImage(item) {
  const fd = item?.facility_details || {};
  const images = fd.facilityImages || fd.facility_images || fd.facilityImageUrls || fd.facility_image_urls;
  if (typeof images === "string" && images.trim()) {
    const url = normalizeImageRef(images.trim());
    if (url) return url;
  }
  if (Array.isArray(images)) {
    for (let i = 0; i < images.length; i += 1) {
      const url = normalizeImageRef(images[i]);
      if (url) return url;
    }
  }
  const banner = normalizeImageRef(fd.bannerImageUrl || fd.banner_image_url || fd.listingBannerUrl);
  if (banner) return banner;

  const prod = item?.product_details || {};
  if (Array.isArray(prod.products)) {
    for (let j = 0; j < prod.products.length; j += 1) {
      const product = prod.products[j];
      const url = normalizeImageRef(
        product.productCatalogueUrl ||
          product.product_catalogue_url ||
          product.productCompanyLogoUrl ||
          product.product_company_logo_url,
      );
      if (url) return url;
    }
  }
  const rootCatalogue = normalizeImageRef(
    prod.productCatalogueUrl || prod.product_catalogue_url || prod.product_catalogue,
  );
  return rootCatalogue || null;
}

const looksLikeLocationId = (value) => {
  if (value == null || value === "") return false;
  return /^\d+$/.test(String(value).trim());
};

export function getManufacturerLocation(item) {
  const fd = item?.facility_details || {};
  const cityLabel = fd.cityName || fd.city_name || fd.cityLabel || fd.displayCity;
  const stateLabel = fd.stateName || fd.state_name || fd.stateLabel || fd.displayState;
  if (cityLabel || stateLabel) {
    return [cityLabel, stateLabel].filter(Boolean).join(", ");
  }

  const cityRaw = fd.city ?? fd.address?.city;
  const stateRaw = fd.state ?? fd.address?.state;
  const addr = item?.address || fd.address;

  if (!looksLikeLocationId(cityRaw) && !looksLikeLocationId(stateRaw)) {
    if (cityRaw || stateRaw) return [cityRaw, stateRaw].filter(Boolean).join(", ");
  }

  if (typeof addr === "string" && addr.trim()) return addr.trim();
  if (addr && typeof addr === "object") {
    const partsNamed = [
      addr.city_name || addr.cityName,
      addr.state_name || addr.stateName,
      addr.country_name || addr.countryName,
    ].filter(Boolean);
    if (partsNamed.length) return partsNamed.join(", ");
  }
  return "";
}

export function getRatingDisplay(item) {
  const fd = item?.facility_details || {};
  const raw = fd.ratingScore ?? fd.rating_score ?? fd.avgRating ?? item?.rating_score ?? item?.rating;
  const value = raw != null && raw !== "" ? Number(raw) : NaN;
  if (!Number.isFinite(value) || value <= 0) return null;
  return value.toFixed(1);
}

export function getReviewCount(item) {
  const fd = item?.facility_details || {};
  const raw = fd.reviewCount ?? fd.review_count ?? item?.review_count;
  const value = raw != null && raw !== "" ? parseInt(String(raw), 10) : NaN;
  if (!Number.isFinite(value) || value < 0) return null;
  return value;
}

export function isFeaturedManufacturer(item) {
  const fd = item?.facility_details || {};
  return Boolean(
    item?.featured ||
      item?.is_featured ||
      fd.featured ||
      fd.isFeatured ||
      fd.listing_featured ||
      fd.listingFeatured,
  );
}

export function getCompletedOrders(item) {
  const fd = item?.facility_details || {};
  const pd = item?.production_details || {};
  const prod = item?.product_details || {};
  const raw =
    fd.completedOrders ??
    fd.completed_orders ??
    pd.completedOrders ??
    pd.completed_orders ??
    prod.completedOrders ??
    prod.completed_orders ??
    item?.completed_orders;
  if (raw == null || raw === "") return null;
  const value = parseInt(String(raw), 10);
  return Number.isFinite(value) ? value.toLocaleString() : String(raw);
}

export function getIndustryTags(item, industryFilterOptions = [], max = 3) {
  const fd = item?.facility_details || {};
  const raw = fd.industryTypes || fd.industry_types || [];
  if (!Array.isArray(raw) || raw.length === 0) return [];

  const resolveLabel = (value) => {
    if (!value) return "";
    const normalized = String(value).trim();
    const fromApi = industryFilterOptions.find(
      (opt) => opt.value === normalized || opt.label === normalized,
    );
    if (fromApi) return fromApi.label;
    if (typeof value === "object" && value?.label) return String(value.label).trim();
    return normalized;
  };

  return raw
    .slice(0, max)
    .map((entry) => resolveLabel(typeof entry === "string" ? entry : entry?.value || entry?.label || entry))
    .filter(Boolean);
}
